import http from '@/api/http.ts'
import type { Format, TopicTag } from '@/types/formats.ts'
import type { Filter, StrapiPopulate, StrapiQueryParams, StrapiResponse } from '@/types/strapi.ts'

/**
 * Create a new format
 * @param data - Partial format data to create
 * @returns Promise resolving to the created format with all populated relations
 */
export async function createFormat(data: Partial<Format>): Promise<StrapiResponse<Format>> {
    return (await http.post('/formats', { data }, { params: { populate: '*' } })).data
}

/**
 * Update an existing format
 * @param id - Format documentId to update
 * @param data - Partial format data to update
 * @returns Promise resolving to the updated format with all populated relations
 */
export async function updateFormat(
    id: string,
    data: Partial<Format>
): Promise<StrapiResponse<Format>> {
    return (await http.put(`/formats/${id}`, { data }, { params: { populate: '*' } })).data
}

/**
 * Delete a format
 * @param id - Format documentId to delete
 * @returns Promise resolving when the format is deleted
 */
export async function deleteFormat(id: string): Promise<void> {
    if (!id) {
        throw new Error('Format id is required')
    }

    await http.delete(`/formats/${id}`)
}

/**
 * Deep populate config for the format editor (FormatChapterizedLayout and similar views).
 * Uses explicit relations instead of '*' so that nested relations like company.logo are included.
 * '*' only does a shallow populate in Strapi and would omit company.logo.
 */
export const FORMAT_EDITOR_POPULATE: StrapiPopulate = {
    company: { populate: ['logo', 'location'] },
    events: true,
    job_offers: true,
    image: true,
    video: true,
    topicTags: true,
    institutions: { populate: ['logo'] },
    application_form: true,
    hostingAddress: true,
    documents: true,
    format_revision: true,
    participationRequirementsFile: true,
}

export const FORMAT_FIND_ONE_POPULATE_FOR_PUBLIC: StrapiPopulate = {
    company: { populate: ['logo', 'location'] },
    events: true,
    job_offers: true,
    image: true,
    video: true,
    topicTags: true,
    institutions: { populate: ['logo'] },
    application_form: true,
    hostingAddress: true,
    documents: true,
}

export async function getFormat(
    id: string,
    status: 'draft' | 'published' = 'published',
    populate: '*' | StrapiPopulate = '*'
): Promise<StrapiResponse<Format>> {
    // Status parameter supports draft preview for organizers and enforces published-only access for participants.
    // We should keep the "populate '*'" at the moment here because this function is used by several views
    return (await http.get(`/formats/${id}`, { params: { populate, status } })).data
}

export async function getFormatWithSlug(
    slug: string,
    params: StrapiQueryParams = {}
): Promise<Format | undefined> {
    return await getFormats({ slug }, params).then((res) => res.data[0])
}
export interface FormatValidateResponse {
    valid: boolean
    errors?: string[]
}

// Kept intentionally for now: frontend calls were removed because backend endpoint
// currently does not provide meaningful validation feedback.
export async function validateFormat(id: string): Promise<FormatValidateResponse> {
    return (await http.get(`/formats/${id}/validate`)).data
}

export async function publishFormat(id: string): Promise<void> {
    if (!id) {
        throw new Error('Format id is required')
    }
    await http.post(`/formats/${id}/publish`)
}

export async function endFormatPublication(id: string): Promise<void> {
    if (!id) {
        throw new Error('Format id is required')
    }
    await http.post(`/formats/${id}/end-publication`)
}

export async function getFormats(
    filters?: Filter<Format>,
    params: StrapiQueryParams = {},
    search?: string
): Promise<StrapiResponse<Format[]>> {
    // Draft status is required so organizer dashboards can include unpublished formats.
    return (
        await http.get<StrapiResponse<Format[]>>('/formats', {
            params: {
                filters,
                populate: {
                    events: true,
                    image: true,
                    company: true,
                    topicTags: true,
                },
                status: 'draft',
                ...params,
                ...(search ? { search } : {}),
            },
        })
    ).data
}

export async function getFormatsWithTopicTags(): Promise<StrapiResponse<Format[]>> {
    return (await http.get('/formats', { params: { populate: 'topicTags' } })).data
}
export async function getTopicTags(params?: {
    pagination?: { pageSize?: number }
}): Promise<StrapiResponse<TopicTag[]>> {
    const mergedParams = { pagination: { pageSize: 1000 }, ...params }
    return (await http.get('/topic-tags', { params: mergedParams })).data
}

export async function fetchSuggestions(search: string) {
    // Suggestions should never surface expired formats, independent of any list filter.
    return (
        await http.get('/format-suggestions', {
            params: {
                search,
                filters: { $or: [{ expired: { $null: true } }, { expired: { $eq: false } }] },
            },
        })
    ).data
}
